import { useMe } from '$/hooks/useMe';
import isMobile from '$/utils/isMobile';
import React from 'react';

/*
TODO: Make it so we can easily add behavior to these theme objects with getters

class ThemeBase {
  public constructor() {}
}

function clone<T extends ThemeBase>(instance: T): T {
  const copy = new (instance.constructor as { new (): T })();
  Object.assign(copy, instance);
  return copy;
}
*/

export interface BorderedT {
  border: string;
}

export interface AreaT {
  bg: string;
  fg: string;
  bgHover?: string;
  fgHover?: string;
  border?: string;
  borderHover?: string;
}

export interface HoverAreaT extends AreaT {
  bgHover: string;
  fgHover: string;
}

export interface ButtonT extends HoverAreaT {
  fgIcon?: string;
  borderSelected?: string;
  bgSelected?: string;
  fgSelected?: string;
}

export interface HoverTextT {
  fg: string;
  fgHover: string;
  placeholder: string;
  bg?: string;
}

export interface ControlT {
  label: {
    fg: string;
    fgHover: string;
    fgIcon: string;
    bg?: string;
  };
  value: HoverTextT;
}

export interface TextT {
  light: string;
  dark: string;
}

interface Theme {
  rangeColors: string[];
  brand: {
    teal: HoverAreaT;
    coral: HoverAreaT;
  };
  correct: TextT;
  incorrect: TextT & { gray: string };

  darkArea: HoverAreaT & { text: TextT };
  grayArea: HoverAreaT & { text: TextT };
  lightArea: HoverAreaT & { text: TextT };
  whiteArea: HoverAreaT & { text: TextT };
  header: HoverAreaT & {
    topBar: {
      bg: string;
      fg: string;
      slider: HoverAreaT;
    };
    tab: HoverAreaT & {
      active: HoverAreaT & BorderedT;
    };
  };
  controls: {
    badge: {
      bg: string;
      fg: string;
    };
    border: {
      normal: string;
      selected: string;
      unselected: string;
    };
    button: {
      basic: ButtonT;
      ops: ButtonT;
      link: ButtonT;
      branded: ButtonT;
      ai: ButtonT;
      row: ButtonT;
      bordered: ButtonT;
      action: ButtonT;
      invalid: ButtonT;
      confirm: ButtonT;
      add: ButtonT;
      delete: ButtonT;
      borderedDelete: ButtonT;
      linkDelete: ButtonT;
      choice: ButtonT;
    };
    input: AreaT & {
      placeholder: string;
      bgActive?: string;
      fgActive?: string;
    };
  };

  home: {
    page: AreaT;
    filters: {
      control: ControlT;
      button: ButtonT;

      brandedControl: HoverAreaT;
      darkControl: HoverAreaT;
    };
    card: AreaT & {
      border: string;
      menu: HoverAreaT;
    };
  };

  player: {
    bg: string;
    fg: string;
    timeline: {
      marker: {
        fg: string;
      };
    };
    card: {
      border: string;
    };
    dock: {
      iconBg: string;
      cursor: string;
      border: string;
    };
    error: AreaT;
    controls: AreaT & { border: string };
  };
}
const rangeColors: Pick<Theme, 'rangeColors'> = {
  rangeColors: ['#39a1b2', '#3ecf8e', '#fed14c'],
};
const correct: Pick<Theme, 'correct'> = {
  correct: {
    dark: '#3D9836',
    light: '#B5E1B3',
  },
};
const incorrect: Pick<Theme, 'incorrect'> = {
  incorrect: {
    dark: '#D50D00',
    light: '#FFD6D5',
    gray: '#DADADA',
  },
};

const brand: Pick<Theme, 'brand'> = {
  brand: {
    teal: { bg: '#39A1B2', bgHover: '#39A1B2A0', fg: '#ffffff', fgHover: '#ffffff' },
    coral: { bg: '#FF7470', bgHover: '#ff615c', fg: '#ffffff', fgHover: '#dddddd' },
  },
};

// Unused so far but header looks good with it
// Dark theme could use this as replacement for one of below areas
const darkArea: Pick<Theme, 'darkArea'> = {
  darkArea: {
    bg: '#808e90',
    bgHover: '#e1e4e5',
    fg: '#fff',
    fgHover: '#edf2f7',
    text: { dark: '#fff', light: '#ddd' },
  },
};

// Used in dialogs
const grayArea: Pick<Theme, 'grayArea'> = {
  grayArea: {
    bg: '#fff',
    bgHover: '#edf2f7',
    fg: '#808e90',
    fgHover: '#e1e4e5',
    text: { dark: '#fff', light: '#ddd' },
  },
};

// Used on pages like /home and /dashboard around the white container areas
const lightArea: Pick<Theme, 'lightArea'> = {
  lightArea: {
    bg: '#F1F5F5',
    fg: '#0e282d',
    bgHover: '#fff',
    fgHover: '#0d213c',
    text: { dark: '#0e282d', light: '#808e90' },
  },
};

// Used in container areas
const whiteArea: Pick<Theme, 'whiteArea'> = {
  whiteArea: {
    bg: '#fff',
    bgHover: '#edf2f7',
    fg: '#808e90',
    fgHover: '#e1e4e5',
    text: { dark: '#808e90', light: '#808e90' },
  },
};

const common: Omit<Theme, 'header'> = {
  /*darkBg: {
    bg: '#3d516c',
    bgHover: '#0d213c',
    fg: '#fff',
  },*/
  ...rangeColors,
  ...brand,
  ...correct,
  ...incorrect,
  ...darkArea,
  ...grayArea,
  ...lightArea,
  ...whiteArea,

  controls: {
    badge: { bg: '#fc3158', fg: '#fff' },
    border: { normal: '#9fa6a8', selected: '#39a1b2', unselected: '#D1D6D6' },
    input: { bg: '#fff', fg: '#5a5b61', placeholder: '#b8bfbf' },
    button: {
      basic: {
        fg: '#39a1b2',
        fgHover: '#2c6c78',
        fgIcon: '#39a1b2',
        bg: 'transparent',
        bgHover: 'transparent',
        border: '',
        borderHover: '',
      },
      ops: {
        fg: '#7036ef',
        fgHover: '#5b2bc3',
        fgIcon: '#7036ef',
        bg: 'transparent',
        bgHover: 'transparent',
        border: '',
        borderHover: '',
      },
      row: {
        fg: '#307883',
        fgHover: '#4698a4',
        bg: '#fff',
        bgHover: '#edf6f7',
        border: '#bdcfce',
        borderHover: '#bdcfce',
      },
      choice: {
        fg: '#527477',
        fgHover: '#718096',
        fgSelected: '#212121',
        bg: '#FFF',
        bgHover: '#f0f8f9',
        bgSelected: '#f0f8f9',
        border: '#b3e7ef',
        borderHover: '#81d5e2',
        borderSelected: '#39a1b2',
      },
      bordered: {
        fg: '#39a1b2',
        fgHover: '#39a1b2',
        bg: '#FFF',
        bgHover: '#edf2f7',
        border: '#39a1b2',
        borderHover: '#39a1b2',
      },
      branded: {
        fg: '#FFF',
        fgHover: '#FFF',
        bg: '#39a1b2',
        bgHover: '#4698a4',
      },
      ai: {
        fg: '#FFF',
        fgHover: '#FFF',
        bg: '#4299e1',
        bgHover: '#63b3ed',
      },
      link: {
        fg: '#39a1b2',
        fgHover: '#4698a4',
        bg: '',
        bgHover: '',
      },
      action: {
        fg: '#FFF',
        fgHover: '#FFF',
        bg: '#FF7470',
        bgHover: '#ff615c',
      },
      invalid: {
        fg: '#1a202c',
        fgHover: '#fff',
        bg: '#e2e8f0',
        bgHover: '#ff615c',
      },
      add: {
        fg: '#FFF',
        fgHover: '#FFF',
        bg: '#FF7470e0',
        bgHover: '#FF7470',
      },
      delete: {
        fg: '#FFF',
        fgHover: '#FFF',
        bg: '#FF7470',
        bgHover: '#ff615c',
      },
      borderedDelete: {
        fg: '#FF7470',
        fgHover: '#ff615c',
        bg: '#FFF',
        bgHover: '#FFF',
        border: '#FF7470',
        borderHover: '#FF7470',
      },
      linkDelete: {
        fg: '#FF7470',
        fgHover: '#ff615c',
        bg: '',
        bgHover: '',
      },
      confirm: {
        fg: '#FFF',
        fgHover: '#FFF',
        bg: '#FF7470',
        bgHover: '##ff615c',
      },
    },
  },

  home: {
    filters: {
      control: {
        label: {
          fg: lightArea.lightArea.fg,
          fgHover: lightArea.lightArea.fg,
          fgIcon: brand.brand.teal.bg,
        },
        value: {
          fg: brand.brand.teal.bg,
          fgHover: brand.brand.teal.bgHover,
          placeholder: brand.brand.teal.bg,
        },
      },
      button: {
        fg: '#5a5b61',
        fgHover: '#5a5b61',
        fgIcon: '#39a1b2',
        bg: 'transparent',
        bgHover: '#fff',
        border: 'transparent',
        borderHover: '#bdcfce',
      },

      brandedControl: {
        ...brand.brand.teal,
      },
      darkControl: {
        ...darkArea.darkArea,
      },
    },
    page: { bg: '#F1F5F5', fg: '#0e282d' },
    card: {
      bg: '#ffffff',
      fg: '#41506A',
      border: '#C6D8DC',
      menu: {
        ...brand.brand.teal,
        fgHover: '#dceff3',
      },
    },
  },

  player: {
    bg: '#f3f3f3',
    fg: '#41506A',
    error: {
      bg: '#00000080',
      fg: '#ffffff',
    },
    timeline: {
      marker: {
        fg: '',
      },
    },
    card: {
      border: '#e5e5e5',
    },
    dock: {
      iconBg: '#ffffff',
      cursor: '#41506A',
      border: '#cbd5e0',
    },
    controls: {
      bg: '#fff',
      fg: '#41506A',
      border: '#41506A',
    },
  },
};

const lightHeader: Pick<Theme, 'header'> = {
  header: {
    bg: '#ffffff',
    bgHover: '#6CB3C4',
    fg: '#0e282d',
    fgHover: '#0d213c',
    topBar: {
      bg: '#3d516c',
      fg: '#ffffff',
      slider: {
        bg: '#3d516c',
        bgHover: '#718096',
        fg: '#ffffff',
        fgHover: '#ffffff',
      },
    },

    tab: {
      bg: '#fff',
      bgHover: '#F1F5F5',
      fg: '#537175',
      fgHover: '#39a1b2',
      active: { ...lightArea.lightArea, fg: '#39a1b2', border: '#C6D8DC' },
    },
  },
};

const darkHeader: Pick<Theme, 'header'> = {
  header: {
    bg: '#3d516c',
    bgHover: '#0d213c',
    fg: '#ffffff',
    fgHover: 'd0d0d0',
    topBar: {
      bg: '#fff',
      fg: '#537175',
      slider: {
        bg: '#3d516c',
        bgHover: '#0d213c',
        fg: '#ffffff',
        fgHover: 'd0d0d0',
      },
    },
    tab: {
      bg: '#3d516c',
      bgHover: '#718096',
      fg: '#fff',
      fgHover: '#fff',
      active: { ...lightArea.lightArea, border: '#000000ff' },
    },
  },
};

const desktopTheme: Theme = {
  ...common,
  ...lightHeader,
};

const mobileTheme: Theme = {
  ...common,
  ...lightHeader,
};

const darkTheme: Theme = {
  ...common,
  ...darkHeader,
};

export const ThemeContext = React.createContext<Theme>(desktopTheme);

export const useThemeContext = (): Theme => {
  const theme = React.useContext(ThemeContext);
  return theme;
};

export const EWebinarThemeProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = React.useState<Theme>(isMobile ? mobileTheme : desktopTheme);

  const me = useMe();
  React.useEffect(() => {
    if (me?.email === 'd+dark@ewebinar.com') {
      setTheme(darkTheme);
    }
  }, [me]);

  return <ThemeContext.Provider value={theme}>{children}</ThemeContext.Provider>;
};
